'use client';

import { MeldungsZeichen, toast } from '../shadcn/sonner';

/**
 * Wie lange ein Häkchen der Stufe 1 steht — P1: „Ein Häkchen am Feld, 1,5 s."
 *
 * EINE Zahl für alle Träger: die Rollen-Zelle der Mitglieder-Liste (J4) und
 * das Kopier-Häkchen des Auftragsverarbeitungs-Hinweises. Sie stand bis
 * #1329 T25b in `live-feld.logik.ts` und ist mit dem gelöschten `LiveFeld`
 * hierher gezogen — an die Datei, die die Stufen führt.
 */
export const HAEKCHEN_MS = 1500;

/**
 * Die vier Stärken der Rückmeldung — Blatt P1.
 *
 * 🔴 WAS HIER DER LIEFERGEGENSTAND IST. Blatt P sagt es selbst: „Ein
 * Hinweis-System ist vorhanden und wird breit genutzt. Was fehlt, ist die
 * ZUORDNUNG: heute bekommt jede Aktion denselben Hinweis, unabhängig von ihrer
 * Folge. Die Tabelle ist deshalb der eigentliche Liefergegenstand — nicht die
 * Optik." Gemessen waren es bei der Aufnahme sehr viele gleich laute Aufrufe.
 *
 * 🔴 Hier stand eine ZAHL („575"), und sie war vier Staende alt. Sie ist raus
 * statt nachgezogen: dieses Bauteil ist nicht die Stelle, an der die Menge
 * gefuehrt wird. Wer sie braucht, faehrt
 * `node apps/web/scripts/mess-hinweise.mjs` — dort wird sie gerechnet und
 * gegen die Spec verglichen. Eine Zahl in einem Docblock, die niemand prueft,
 * wandert genau so lange mit, bis jemand sie abschreibt.
 *
 * Die Leitregel: **eine Meldung ist so stark wie ihre Folge.**
 *
 *   Stufe 1  still            Ein Häkchen am Feld, 1,5 s. Für Live-Editing (A4).
 *                             Ein Hinweis je gespeichertem Feld wäre nach zehn
 *                             Minuten Arbeit ein Trommelfeuer.
 *   Stufe 2  Hinweis, 5 s     Für Wirkung nach AUSSEN, die man nicht sieht:
 *                             E-Mail raus, Einladung raus, Portal aktualisiert.
 *                             IMMER mit Verweis auf das Ergebnis — sonst ist er
 *                             nur eine Behauptung.
 *   Stufe 3  —                GESTRICHEN. Sie hing an einer Warteschlange, die
 *                             mit P6 entfallen ist. Keine Zeile führt mehr
 *                             hierher; die Sicherung liegt jetzt in der
 *                             Bestätigung VOR der Handlung, mit Anzahl und Folge
 *                             im Satz.
 *   Stufe 4  bleibt stehen    Für Zustände, die jemand beheben muss. Kein
 *                             flüchtiger Hinweis, sondern ein Streifen an der
 *                             Stelle, wo es zählt — und zusätzlich in der
 *                             Glocke. Zustand, Folge, Ausweg, in dieser
 *                             Reihenfolge.
 *
 * Stufe 4 ist deshalb KEINE Funktion in dieser Datei: ein Aufruf, der nach fünf
 * Sekunden weg ist, kann sie nicht abbilden. Sie ist der `Systemstreifen` unten.
 */

/**
 * Stufe 1 — still. Kein Hinweis; das Häkchen sitzt am Feld (siehe A4).
 *
 * ===========================================================================
 * 🔴 STUFE 1 SETZT EINEN SICHTBAREN TRÄGER VORAUS — Anweisung 611 (21.09.2026)
 * ===========================================================================
 *
 * Die Zuordnungstabelle in P1 schrieb Stufe 1 als „ein Häkchen am Feld, 1,5 s"
 * — und an einem Panel nach A4 Modus 2 schließt sich beim Speichern genau die
 * Fläche, auf der dieses Häkchen säße. 611 nimmt die Frage an und schärft die
 * Regel:
 *
 * > „P1 setzt Stufe 1 als «Häkchen am Feld» — und beim Panel schließt sich die
 * >  Fläche, auf der es säße. **Nicht «still», sondern ersatzlos.**"
 *
 * **Die drei Lagen, wie 611 sie entscheidet:**
 *
 * | Lage | Beleg |
 * |---|---|
 * | Panel schließt sich | **Das Schließen IST der Beleg.** Kein Häkchen an der Karte — „ein Zeichen an einer Überschrift, aus der man gerade kam, ist schwerer zu deuten als gar keins" |
 * | Wert getippt, unverändert | **Kein Zeichen.** „Es ist nichts passiert, und dafür ist kein Beleg nötig" |
 * | Das geänderte Feld steht nicht in der Leseansicht | **Stufe 2**, unten links („Gespeichert") — „sieht man nichts, auch beim echten Speichern nicht" |
 *
 * **Und die Regel in einem Satz, wörtlich aus 611:** *„Stufe 1 setzt einen
 * sichtbaren Träger voraus. Fehlt er, gilt Stufe 2 — und wo das Ergebnis
 * selbst sichtbar wird, gilt keine Stufe."* Der letzte Halbsatz ist
 * `keineMeldungWeilSichtbar()` weiter unten, nicht diese Funktion.
 *
 * ⚠️ **Die Zeile „Kriterium im Panel gespeichert · 1" gilt auch fürs
 * Exposé-Panel** — keine eigene Tabellenzeile, „es ist dieselbe Bauart (A4,
 * Modus 2)".
 *
 * 🔴 **Wo die dritte Lage gebaut ist und was 611s Beispiele dort NICHT
 * halten**, steht an der Codestelle:
 * `apps/web/app/home/[account]/rentals/cases/[id]/_components/rental-case-overview-content.tsx`.
 * Diese Datei ist die falsche für eine Liste unsichtbarer Felder — sie kennt
 * keine Fläche.
 */
export function stufe1Still() {
  // Absichtlich leer und trotzdem vorhanden: so steht im Aufrufcode, dass die
  // Stufe BEDACHT wurde. Ein fehlender Aufruf ist von einem vergessenen nicht
  // zu unterscheiden.
}

/**
 * Stufe 2 — Hinweis unten links, 5 Sekunden, mit Verweis auf das Ergebnis.
 *
 * 🔴 `ergebnis` ist Pflicht. P1: „Immer mit Verweis auf das Ergebnis — sonst ist
 * er nur eine Behauptung." „Absage an Thomas Neumann verschickt" ohne ein
 * „Ansehen" daneben ist eine Behauptung, die man nicht prüfen kann.
 */
export function stufe2Hinweis({
  text,
  ergebnis,
}: {
  text: string;
  ergebnis: {
    label: string;
    onClick: () => void;
    /**
     * Klasse am Verweis — `amTelefonVerborgen(ziel)` aus dem Telefon-Umfang,
     * wenn das Ziel eine Rechner-Fläche ist (#1329 T28). Die Regel in
     * `shadcn-ui.css` blendet ihn dann unter 768 px aus; die Meldung selbst
     * bleibt.
     */
    className?: string;
  };
}) {
  toast(text, {
    duration: 5000,
    /*
     * 🔴 DAS ZEICHEN MUSS HIER MITGEGEBEN WERDEN — sonst gibt es keines.
     *
     * Am 10.09.2026 auf staging an einer echten Meldung gemessen: der Bau
     * traegt gar kein `[data-icon]`. Der Grund ist die Bauart dieser Stufe.
     * Sonner zeichnet ein Zeichen nur fuer die TYPEN (`success`, `error`, …);
     * Stufe 2 ist bewusst TYPLOS, weil ihre Farbe aus `--normal-bg` kommen
     * soll und nicht aus einer Zustandsfarbe. Damit faellt sonners Zeichen
     * weg — und die Regel in `shadcn-ui.css`, die es einfaerbt, lief ins
     * Leere: ein Waechter auf einem Element, das nie entsteht.
     *
     * P1 zeichnet den Haken aber ausdruecklich: Kreis plus Haken
     * (`m9 12 2 2 4-4` — lucide `CircleCheck`, im Blatt zeichengleich),
     * 17 px, in `oklch(0.78 0.15 150)` — GRUEN. Seit R-31 (06.10.2026) ist er
     * es wieder: dasselbe Erfolgszeichen wie `toast.success`
     * (`MeldungsZeichen ton="ok"`), denn Stufe 2 meldet eine erledigte
     * Handlung.
     */
    icon: <MeldungsZeichen ton="ok" />,
    action: { label: ergebnis.label, onClick: ergebnis.onClick },
    classNames: ergebnis.className
      ? { actionButton: ergebnis.className }
      : undefined,
  });
}

/**
 * Stufe 2, stehend — dieselbe Form wie `stufe2Hinweis`, aber ohne Ablauf.
 *
 * Für einen Zustand, der bleibt, bis jemand handelt, und der keine Stelle in der
 * Seite hat, an der ein `Systemstreifen` stehen könnte: „Neue Version verfügbar"
 * (Auftraggeber 13.10.2026). Nach fünf Sekunden weg, arbeitete man sonst mit
 * dem alten Stand weiter. Die feste `id` hält es bei EINEM Hinweis, auch wenn
 * der Auslöser mehrfach feuert.
 */
export function stufe2Stehend({
  id,
  text,
  ausweg,
}: {
  id: string;
  text: string;
  ausweg: { label: string; onClick: () => void };
}) {
  toast(text, {
    id,
    duration: Infinity,
    closeButton: true,
    icon: <MeldungsZeichen ton="info" />,
    action: { label: ausweg.label, onClick: ausweg.onClick },
  });
}

/**
 * Sammelaktionen fassen zusammen — P1: „‚3 Absagen', nicht dreimal ‚1 Absage'."
 *
 * Getrennte Funktion statt einer Zählschleife im Aufrufcode, weil das die
 * Stelle ist, an der es im Bestand auseinanderläuft: drei Aufrufe in einer
 * Schleife sind einfacher zu schreiben als einer mit einer Zahl.
 */
export function stufe2Sammel({
  anzahl,
  text,
  ergebnis,
}: {
  anzahl: number;
  /** Bekommt die Anzahl — „{n} Absagen verschickt". */
  text: (anzahl: number) => string;
  ergebnis: { label: string; onClick: () => void };
}) {
  stufe2Hinweis({ text: text(anzahl), ergebnis });
}

/**
 * Fehler nach einer Aktion — Blatt P2, Art 2.
 *
 * 🔴 Der wichtigste Satz gehört in `text`: „Deine Eingaben sind noch da." P2:
 * „Die Angst bei einem Fehlschlag ist nicht der Fehler, sondern der Verlust."
 *
 * Und deshalb ist das KEIN flüchtiger Hinweis — er würde verschwinden, bevor man
 * ihn gelesen hat. Der Fehler steht dort, wo die Aktion war; der Knopf bleibt
 * bedienbar. Diese Funktion existiert nur, um genau das zu sagen: für Art 2
 * gibt es keinen Toast. Sie wirft in der Entwicklung.
 */
export function keinToastFuerAktionsfehler(): never {
  /*
   * 🔴 `AktionsFehler` UND NICHT `FehlerZustand` — berichtigt am 10.09.2026.
   *
   * Hier stand „Nimm `FehlerZustand` aus @kit/ui/zustaende". Gemessen ueber
   * den ganzen Baum: `FehlerZustand` hat EINEN Nutzer
   * (`kurzfristig-aktionen.tsx:102`) und eine andere Bedeutung — es traegt
   * einen „nochmal versuchen"-Knopf. Der gelebte Weg fuer P2 Art 2 ist
   * `AktionsFehler` (`zustaende.tsx`) mit 135 Aufrufen in 105 Dateien.
   *
   * Eine Wurf-Meldung ist eine Wegweisung, und diese wies auf den falschen
   * Weg: wer sie liest, steht vor der Wahl zwischen einem Bauteil mit einem
   * Nutzer und einem mit 135 — und nimmt das genannte.
   */
  throw new Error(
    'Blatt P2, Art 2: ein fehlgeschlagener Aktionsaufruf bekommt KEINEN ' +
      'fluechtigen Hinweis, sondern eine Meldung unter dem Knopf — er wuerde ' +
      'verschwinden, bevor man ihn gelesen hat. Nimm `AktionsFehler` aus ' +
      '@kit/ui/zustaende oder eine eigene Zeile unter der Aktion.',
  );
}

/**
 * Die Rücknahme nach A9 — Blatt 02 und Blatt 05, wörtlich: „Löschen fragt
 * nicht nach, sondern zeigt 8 Sekunden «Bild gelöscht · Rückgängig» (A9)."
 *
 * 🔴 DAS IST KEINE STUFE. Sie trägt eine HANDLUNG statt einer Mitteilung, und
 * sie ist NICHT die gestrichene Stufe 3: die galt für Sammelaktionen und hing
 * an einer Warteschlange für den Massenversand (P6, Punkt 2). Eine einzelne,
 * lokal aufgeschobene Löschung hat ihre Warteschlange schon — einen
 * `setTimeout` im Aufrufcode.
 *
 * 🔴 DESHALB TRÄGT SIE DIE 8 SEKUNDEN UND NICHT `stufe2Hinweis`. Wer die
 * Dauer dort einbaut, öffnet sie für jede Meldung; P1 setzt 5 s fest, und
 * eine Meldung, die länger steht als die nächste Handlung dauert, ist im Weg.
 *
 * ⚠️ Kein Zeichen. Der Haken der Stufe 2 sagt „erledigt"; hier ist gerade
 * NICHT erledigt, sondern aufgeschoben — das ist der ganze Sinn der Form.
 */
export function ruecknahmeHinweis({
  text,
  rueckgaengig,
}: {
  text: string;
  rueckgaengig: { label: string; onClick: () => void };
}) {
  toast(text, {
    duration: 8000,
    action: { label: rueckgaengig.label, onClick: rueckgaengig.onClick },
  });
}

/**
 * F3 — die elfte Zeile (Blatt 00, Antwort 1): „Ergebnis sichtbar ⇒ keine
 * Meldung." Tut nichts, aus demselben Grund wie `stufe1Still()`.
 *
 * 🔴 Und aus einem zusätzlichen: das ist der Ausgang, an dem eine Meldung
 * ERSATZLOS verschwindet. „Zu still" fällt am Bildschirm nicht auf — es fehlt
 * nichts, was man sehen könnte. Ein fehlender Aufruf ist von einem vergessenen
 * nicht zu unterscheiden; diese Marke macht den Ausgang zählbar.
 *
 * `was` ist Pflicht und nennt die sichtbare Veränderung, die den Beleg trägt —
 * „die Zeile ist weg", „das Bild liegt da". Wer sie nicht benennen kann, hat
 * nicht F3, sondern F4.
 *
 * ⚠️ Das Argument wird NICHT gelesen, und das ist Absicht: es zwingt beim
 * Schreiben zu genau dem Satz, den die Abnahme ohnehin braucht. Deshalb der
 * Unterstrich — die Lint-Regel für ungenutzte Argumente ist hier kein Befund,
 * sondern die Bauform.
 */
export function keineMeldungWeilSichtbar(_was: string) {}
